/**
 * appVersion — single source of truth for the human-facing release version and
 * build identifier shown in the UI and stamped onto every bug report.
 *
 * Two distinct values, deliberately NOT conflated:
 *   - APP_VERSION — the semver release (`1.1.0`), baked from package.json via
 *     vite.config.ts `define` block (`__APP_VERSION__`). Bump it in ONE place
 *     (package.json) and it flows everywhere.
 *   - BUILD_SHA — the short git SHA of the exact build, via `getBuildSha()`
 *     (`__BUILD_HASH__`, set by cloudbuild.yaml / git fallback).
 *
 * Every consumer (login page, app chrome, bug-report submit) imports from HERE
 * so the displayed/stamped values can never drift between surfaces.
 *
 * Resolution mirrors `runtimeFlags.getBuildSha`: read the Vite-substituted
 * global, honour a `globalThis.__APP_VERSION__` override (so tests and dev-
 * console can exercise branches without rebuilding), and fall back to a safe
 * `'unknown'` outside a Vite bundle (older Node / SSR / certain test paths).
 */
import { getBuildSha } from './runtimeFlags';

declare const __APP_VERSION__: string;

/**
 * The semver release version baked into the bundle at build time
 * (`__APP_VERSION__`). Falls back to `'unknown'` when the global is
 * unavailable (non-Vite context).
 */
function resolveAppVersion(): string {
  // Honour a globalThis override first so Vitest / dev-console can set it
  // without a rebuild (mirrors getBuildSha / getGenaiBackend).
  try {
    const g = globalThis as unknown as { __APP_VERSION__?: unknown };
    if (typeof g.__APP_VERSION__ === 'string' && g.__APP_VERSION__.length > 0) {
      return g.__APP_VERSION__;
    }
  } catch {
    /* swallow */
  }

  try {
    if (typeof __APP_VERSION__ === 'string' && __APP_VERSION__.length > 0) {
      return __APP_VERSION__;
    }
  } catch {
    /* __APP_VERSION__ undefined outside Vite — fall through */
  }

  return 'unknown';
}

/** Semver release version, e.g. `1.1.0`. */
export const APP_VERSION: string = resolveAppVersion();

/** Short git SHA of the running build, e.g. `a1b2c3d` (or `unknown`). */
export const BUILD_SHA: string = getBuildSha();

/** True when the SHA is missing/unresolved and should be omitted from labels. */
function shaIsKnown(sha: string): boolean {
  return Boolean(sha) && sha.toLowerCase() !== 'unknown';
}

/** Discreet version label for the login page, e.g. `v1.1.0`. */
export const VERSION_LABEL = `v${APP_VERSION}`;

/**
 * Full version label for app chrome, e.g. `v1.1.0 · a1b2c3d`. The `· <sha>`
 * suffix is omitted entirely when the SHA is falsy / `'unknown'`, so the label
 * degrades gracefully to just `v1.1.0` rather than `v1.1.0 · unknown`.
 */
export const VERSION_LABEL_FULL = shaIsKnown(BUILD_SHA)
  ? `${VERSION_LABEL} · ${BUILD_SHA}`
  : VERSION_LABEL;
